import { Injectable } from '@angular/core';

import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppConstants } from '@unpispas/upp-defs';
import { HttpResponseTypes, HttpSerializer } from './http';
import { stateService } from './state';
import { toastService } from './toasts';
import { downloadService } from './upload';
import { languageService } from './language';
import { httpService } from './http';

/**
 * Optional cancel hook for an in-flight `DoRequest`.
 * When set, `abort` is assigned after subscribe and clears the XHR on call.
 */
export type AdhocRequestCancel = {
    abort: (() => void) | null;
};

/**
 * @class adhocService
 * @description Service for handling HTTP requests with additional custom parameters, error handling, and toast notifications.
 */
@Injectable({
    providedIn: 'root'
})
export class adhocService {

    /**
     * @constructor
     * @param {languageService} lang - Service for managing application language settings.
     * @param {stateService} state - Service for managing session and device state information.
     * @param {httpService} http - Service for handling HTTP requests.
     * @param {toastService} toast - Service for displaying toast notifications.
     */    
    constructor(private lang: languageService, private state: stateService, private http: httpService, private toast: toastService, private download: downloadService){
        // nothing to do
    }

    /**
     * @method DoRequest
     * @description Sends an HTTP GET or POST request based on the provided parameters. 
     * It includes additional custom query parameters (device, session, and language), handles errors, and shows toast notifications if needed.
     * 
     * @param {string} url - The URL endpoint for the request.
     * @param {any} [params=null] - Optional query parameters to append to the request URL.
     * @param {any} [post=null] - Optional body for a POST request. If null, a GET request is made.
     * @param {boolean} [showtoast=true] - Whether to show a toast on HTTP transport error (console.error always runs).
     * @param {any} [headers=null] - Optional HTTP headers to include in the request.
     * @param {HttpResponseTypes | null} [responseType=null] - Optional response type for the request.
     * @param {HttpSerializer | null} [serializer=null] - Optional serializer for the request.
     * @param {number | null} [timeout=null] - Optional timeout duration for the request.
     * @param {AdhocRequestCancel | null} [cancel=null] - Optional cancel handle; `abort()` unsubscribes and rejects with `{ status: 0 }`.
     * 
     * @returns {Promise<any>} A promise that resolves with the response data if successful, or rejects with an error message.
     */    
    DoRequest(url: string, params: any = null, post: any = null, showtoast = true, headers: any = null, responseType: HttpResponseTypes | null = null, serializer: HttpSerializer | null = null, timeout: number | null = null, cancel: AdhocRequestCancel | null = null) : Promise <any>{
        let _getparams = ''

        function _sep(_getparams:string){
            return (_getparams.length == 0) ? '?' : '&';
        }

        if (this.state.device){
            _getparams += _sep(_getparams) + "device=" + this.state.device;            
        }

        if (this.state.session){
            _getparams += _sep(_getparams) + "session=" + this.state.session;
        }

        // App work mode (POS | PTF | QRA) — login SESSION.mode, sync datamodel target, etc.
        _getparams += _sep(_getparams) + "client=" + this.state.WorkMode;
        
        const _lang = this.lang.GetLanguage(true);
        if (_lang){
            _getparams += _sep(_getparams) + "lang=" + _lang;
        }

        if (this.state.place){
            _getparams += _sep(_getparams) + "place=" + this.state.place;
        }

        if (params){
            for(const _param in params){
                _getparams += _sep(_getparams) + _param + "=" + params[_param];
            }    
        }
        
        let _request = null;
        if (post){
            _request = this.http.post(AppConstants.baseURL + url + _getparams, post, (headers) ? headers : httpService.headers, responseType, serializer, timeout);
        }
        else {
            _request = this.http.get(AppConstants.baseURL + url + _getparams, true, (headers) ? headers : httpService.headers, responseType, timeout);
        }

        return new Promise((resolve, reject) => {
            let _settled = false;
            let _subscription: Subscription | null = null;

            const _clearCancel = () => {
                if (cancel){
                    cancel.abort = null;
                }
            };

            const _settleReject = (error: any) => {
                if (_settled){
                    return;
                }

                _settled = true;
                _clearCancel();
                reject(error);
            };

            const _settleResolve = (data: any) => {
                if (_settled){
                    return;
                }

                _settled = true;
                _clearCancel();
                resolve(data);
            };

            if (cancel){
                cancel.abort = () => {
                    if (_subscription){
                        _subscription.unsubscribe();
                        _subscription = null;
                    }

                    _settleReject({ status: 0 });
                };
            }

            _subscription = _request.pipe(take(1)).subscribe({
                next: (data) => {
                    if (data !== null){                        
                        const _body: any = data;    // Application-level failure (HTTP 200 + errorcode): always log; toast stays at the caller.
                        if (typeof _body === 'object' && !(typeof Blob !== 'undefined' && _body instanceof Blob) && _body['errorcode'] != null && _body['errorcode'] !== 0) {
                            console.error("[SERVICE] Error [" + _body['errorcode'] + "] in '" + url + "'");
                        }
                        _settleResolve(data);
                    }
                    else {
                        _settleReject("no response");
                    }
                },
                error: (error) => {
                    if (error && error.status != 0){     // Transport failure: always log; toast remains gated by showtoast.
                        console.error(this.lang.tr('@http_request_error_url', [ error.status, url ]));
                        if (showtoast){
                            this.toast.ShowAlert('danger', this.lang.tr('@http_request_error_url', [ error.status, url ]));
                        }
                    }
                    else if (!_settled){
                        console.error("[HTTP] status = 0 (you are offline)");
                    }
                    
                    _settleReject(error);
                },
                complete: () => {
                    // nothing to do
                }
            });
        });
    }

    /**
     * @method Download
     * @description Downloads a file from a given URL, either saving it locally or opening it in a new tab.
     * It also manages UI interactions like showing a loading indicator while the file is being fetched.
     * 
     * @param {string} url - The URL from which the file should be downloaded.
     * @param {string} filename - The desired filename for the downloaded file.
     * @param {any} [headers=null] - Optional HTTP headers to include in the request, used for determining file type.
     * @param {boolean} [download=true] - If `true`, the file will be downloaded. If `false`, it will be opened in a new tab.
     * @param {string | null} [target=null] - If `download` is `false`, this sets the target attribute for opening the file (e.g., `_blank`).
     * 
     * @returns {Promise<boolean>} A promise that resolves to `true` if the file is successfully downloaded or opened, or `false` if the download fails.
     * 
     * @example
     * // Download a file and save it locally
     * await adhocService.Download('https://example.com/file.pdf', 'myfile.pdf');
     * 
     * @example
     * // Open a file in a new tab instead of downloading
     * await adhocService.Download('https://example.com/file.pdf', '', null, false, '_blank');
     */    
    async Download(url: string, filename: string, headers: any = null, download=true, target: string | null = null): Promise <boolean> {
        let data = null;

        console.info("[DOWNLOAD] url: '" + url + "'");

        this.toast.ShowWait();
        try {
            data = await this.download.file(url);
        }
        catch {
            // nothing to do
        }
        this.toast.HideWait();

        if (data){
            const file = new Blob([data], { type: headers ? headers['Content-Type'] : 'application/octet-stream' });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(file);
            console.info("[DOWNLOAD] href: '" + link.href + "'");

            if (!download){     // open file in new page (if target = '_blank')
                if (target) {
                    link.target = target;
                }
            }
            else {          // download the file
                link.download = filename;
            }

            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href)
            }, 60000);  // wait 1 minute

            return true;
        }

        return false;   // no data has been donloaded
    }
}